import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import MatchingSupplierCountSource from '../../../../Data/MatchingSupplierCountSource';
import { MATCHING_SUBCATEGORY_WHITELIST } from '../../../../Data/MatchingSuppliers';
import { isSupplierMatch } from '../../../../Helpers/supplierMatchPredicates';
import {
  formatAnswersForSmartMatchMeter,
  getAllAdmiralQuestionIds,
  getFurthestAnsweredPage,
} from '../../../../Helpers/supplierMatches';
import { getPartialSubmission } from '../../../../Helpers/submission';
import { setMatchingSupplierCountRequestScheduled } from '../../slices/supplierMatches';
import { exposeMatchPoolUpdatedEvent } from '../../../../Helpers/events';
import getMatchingSuppliersQuery from './getMatchingSuppliersQuery';

const getSupplierMatchComponents = (components) =>
  Object.values(components).filter((component) => isSupplierMatch(component));

const shouldSkipSubscriptionRequest = ({
  subcategoryName,
  supplierMatches,
  component,
}) => {
  if (!component?.component?.mappedQuestionId) return true;
  if (!MATCHING_SUBCATEGORY_WHITELIST.includes(subcategoryName)) return true;

  return (
    supplierMatches.matchingSupplierCountSource ===
      MatchingSupplierCountSource.MATCHES ||
    supplierMatches.matchingSupplierCountSource ===
      MatchingSupplierCountSource.FALLBACK
  );
};

export function* refreshMatchingSupplierCountWorker(action) {
  const { componentId } = action.payload;
  const {
    components,
    supplierMatches,
    formConfig,
    features,
    optionalFormConfig,
  } = yield select();
  const { showSmartMatchMeter, showSmartMatchMeterV2, supplierMatchCountUp } =
    features || {};
  const { iFrameId } = optionalFormConfig || {};

  if (!showSmartMatchMeter && !showSmartMatchMeterV2) {
    return;
  }

  const component = components[`component_${componentId}`];
  if (
    shouldSkipSubscriptionRequest({
      subcategoryName: formConfig.subcategoryName,
      supplierMatches,
      component,
    })
  ) {
    return;
  }

  const supplierMatchComponents = getSupplierMatchComponents(components);
  const furthestAnsweredPage = getFurthestAnsweredPage(components);
  const answers = formatAnswersForSmartMatchMeter(
    getPartialSubmission(components),
    supplierMatchComponents,
    supplierMatches.subcategoryMatches,
    components,
    furthestAnsweredPage
  );

  if (answers.length === 0) return;

  const questionsOnForm = getAllAdmiralQuestionIds(components);

  let didDispatchLoading = false;
  try {
    yield put({
      type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
      payload: {
        isLoading: true,
        matchingSupplierCountLoadingSource: MatchingSupplierCountSource.PREVIEW,
      },
    });
    didDispatchLoading = true;

    const { matchingSupplierCount } = yield call(getMatchingSuppliersQuery, {
      variables: {
        subcategoryId: formConfig.subcategoryId,
        answers,
        questionsOnForm,
        countUp: supplierMatchCountUp,
      },
    });

    // Always emit the event, even if the count hasn't changed
    exposeMatchPoolUpdatedEvent({
      source: 'chameleonFilter',
      count: matchingSupplierCount,
      iFrameId,
    });

    // Only update Redux state if the count or source has changed
    if (
      supplierMatches.matchingSupplierCountSource ===
        MatchingSupplierCountSource.PREVIEW &&
      supplierMatches.matchingSupplierCount === matchingSupplierCount
    ) {
      return;
    }

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.PREVIEW,
      },
    });
  } catch (error) {
    // Silently swallow errors for now; rely on customer matches saga to provide fallbacks.
  } finally {
    if (didDispatchLoading) {
      yield put({
        type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
        payload: {
          isLoading: false,
          matchingSupplierCountLoadingSource: null,
        },
      });
    }
  }
}

export function* handleForwardNavigationWorker(action) {
  if (!action?.payload?.shouldNavigateForward) {
    return;
  }

  const { supplierMatches, features, formConfig } = yield select();
  const { showSmartMatchMeter, showSmartMatchMeterV2 } = features || {};

  if (!showSmartMatchMeter && !showSmartMatchMeterV2) {
    return;
  }

  const {
    matchingSupplierCountRequestScheduled,
    matchingSupplierCountRequestComponentId,
    matchingSupplierCountSource,
  } = supplierMatches || {};

  if (!matchingSupplierCountRequestScheduled) {
    return;
  }

  const hasFinalSource =
    matchingSupplierCountSource === MatchingSupplierCountSource.MATCHES ||
    matchingSupplierCountSource === MatchingSupplierCountSource.FALLBACK;

  if (
    !matchingSupplierCountRequestComponentId ||
    hasFinalSource ||
    !MATCHING_SUBCATEGORY_WHITELIST.includes(formConfig?.subcategoryName)
  ) {
    yield put(setMatchingSupplierCountRequestScheduled({ isScheduled: false }));
    return;
  }

  yield put({
    type: ActionTypes.REFRESH_MATCHING_SUPPLIER_COUNT,
    payload: { componentId: matchingSupplierCountRequestComponentId },
  });

  yield put(setMatchingSupplierCountRequestScheduled({ isScheduled: false }));
}

export default function* matchingSuppliersWatcher() {
  yield all([
    takeEvery(
      ActionTypes.REFRESH_MATCHING_SUPPLIER_COUNT,
      refreshMatchingSupplierCountWorker
    ),
    takeEvery(
      'NAVIGATION_CONTROLS/setShouldNavigateForward',
      handleForwardNavigationWorker
    ),
  ]);
}
